import type { IndexAttributeItem } from "@/repo";
import { getEnvParam, type TileEnv } from "@/env";
import ControlPanelListSearch from "../ControlPanelLocation/ControlPanelListSearch";
import {
    useMemo,
    useState,
    type MutableRefObject,
    type RefObject,
    useEffect,
} from "react";
import "../ControlPanelLocation/ControlPanelList.css";
import "./Rank.css";
import { Popup, type Map, type MapMouseEvent } from "mapbox-gl";
import Input from "../atoms/Input";
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    type DragEndEvent,
} from "@dnd-kit/core";
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
    useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
    restrictToVerticalAxis,
    restrictToParentElement,
} from "@dnd-kit/modifiers";
import {
    POWER_MEAN_DEFAULT_P,
    RANK_METHOD_LABELS,
    explainCell,
    explainPlace,
    explainProperty,
    type RankingContext,
    type RankMethod,
    type ScoreBreakdown,
} from "@/repo/rank";
import { RANK_PROFILES } from "@/repo/profiles";
import { parseAnchors, serializeAnchors, type Anchor } from "@/repo/anchors";
import Anchors, { anchorColour } from "./Anchors";
import { PLACE_LEVELS, RANK_TARGET_TABS, type RankTarget } from "@/constants";
import TabList from "../TabList";
import "../ControlPanelLocation/LocationPlaceAttributeList.css";
import { GripVertical, PoundSterling, X } from "lucide-react";
import { createMapboxFillAndOpacity } from "@/constants";
import { useSearchParams } from "react-router-dom";

interface RankProps {
    attributes: IndexAttributeItem[];
    map: RefObject<Map | undefined>;
    rankingContextRef: MutableRefObject<RankingContext>;
    onSelectScore: (score: ScoreBreakdown | null) => void;
    env: TileEnv;
}

// Anchors join the stack rank as pseudo-attributes named "anchor:{index}",
// referencing the anchors array by position. The server resolves them to
// per-cell isochrone accessibility values.
const anchorName = (index: number) => `anchor:${index}`;

function parseAnchorName(name: string): number | null {
    const match = /^anchor:(\d+)$/.exec(name);
    return match ? Number(match[1]) : null;
}

// Set a param to `value`, or delete it when empty, so we never leave behind
// blank `attrs=`/`anchors=` keys in the URL.
function writeParam(params: URLSearchParams, key: string, value: string) {
    if (value) params.set(key, value);
    else params.delete(key);
}

type RankEntry =
    | { kind: "attribute"; name: string }
    | { kind: "anchor"; name: string; anchorIndex: number };

function escapeHtml(s: string): string {
    return s
        .replaceAll("&", "&amp;")
        .replaceAll("<", "&lt;")
        .replaceAll(">", "&gt;")
        .replaceAll('"', "&quot;")
        .replaceAll("'", "&#39;");
}

function SortableItem(props: {
    id: string;
    children: React.ReactNode;
    className?: string;
    onRemove: () => void;
}) {
    const { attributes, listeners, setNodeRef, transform, transition } =
        useSortable({ id: props.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            {...attributes}
            {...listeners}
            className={props.className}
        >
            <div className="rank-item__content">
                <div className="rank-item__handle">
                    <GripVertical size={16} />
                </div>
                <div className="rank-item__text">{props.children}</div>
            </div>
            <button
                className="control-panel-button-list__remove"
                onPointerDown={(e) => {
                    e.stopPropagation();
                }}
                onClick={(e) => {
                    e.stopPropagation();
                    props.onRemove();
                }}
            >
                <X size={16} />
            </button>
        </div>
    );
}

function Rank(props: RankProps) {
    const [searchParams, setSearchParams] = useSearchParams();
    const [search, setSearch] = useState("");

    // All rank configuration lives in URL search params for shareability.
    const rankedNames = useMemo(
        () => (searchParams.get("attrs") ?? "").split(",").filter(Boolean),
        [searchParams],
    );
    const method = (searchParams.get("method") ?? "wpm") as RankMethod;
    const maxBuyingPower = searchParams.get("budget") ?? "";
    const pInput = searchParams.get("p") ?? POWER_MEAN_DEFAULT_P.toString();
    const target = (searchParams.get("target") ?? "tokens") as RankTarget;
    const placeLevel = Number(
        searchParams.get("level") ?? PLACE_LEVELS[0]?.value ?? 2,
    );
    const anchorsParam = searchParams.get("anchors");
    const anchors = useMemo(() => parseAnchors(anchorsParam), [anchorsParam]);
    // Household composition for the property size/room match score.
    const adultsParam = searchParams.get("adults") ?? "";
    const child05Param = searchParams.get("c05") ?? "";
    const child612Param = searchParams.get("c612") ?? "";
    const child13Param = searchParams.get("c13") ?? "";

    function updateParams(updates: Record<string, string | null>) {
        setSearchParams(
            (prev) => {
                const next = new URLSearchParams(prev);
                for (const [key, value] of Object.entries(updates)) {
                    if (value === null || value === "") {
                        next.delete(key);
                    } else {
                        next.set(key, value);
                    }
                }
                return next;
            },
            { replace: true },
        );
    }

    // Stack-rank and anchor edits read the *live* URL inside the updater rather
    // than closing over `rankedNames`/`anchors`. A stale snapshot was what made
    // adding an anchor (or any edit through a captured handler) silently wipe
    // the rest of the rank. Return only the slices you want to change.
    function mutateStack(
        edit: (state: { attrs: string[]; anchors: Anchor[] }) => {
            attrs?: string[];
            anchors?: Anchor[];
        },
    ) {
        setSearchParams(
            (prev) => {
                const next = new URLSearchParams(prev);
                const result = edit({
                    attrs: (prev.get("attrs") ?? "")
                        .split(",")
                        .filter(Boolean),
                    anchors: parseAnchors(prev.get("anchors")),
                });
                if (result.attrs !== undefined)
                    writeParam(next, "attrs", result.attrs.join(","));
                if (result.anchors !== undefined)
                    writeParam(
                        next,
                        "anchors",
                        serializeAnchors(result.anchors),
                    );
                return next;
            },
            { replace: true },
        );
    }

    // Resolved stack-rank entries, in rank order: attributes that exist plus
    // anchor references that point at a live anchor.
    const rankedItems = useMemo<RankEntry[]>(
        () =>
            rankedNames.flatMap((name): RankEntry[] => {
                const anchorIndex = parseAnchorName(name);
                if (anchorIndex !== null) {
                    return anchorIndex < anchors.length
                        ? [{ kind: "anchor", name, anchorIndex }]
                        : [];
                }
                return props.attributes.some((a) => a.name === name)
                    ? [{ kind: "attribute", name }]
                    : [];
            }),
        [rankedNames, anchors.length, props.attributes],
    );

    const availableAttributes = useMemo(
        () =>
            props.attributes
                .filter((a) => !rankedNames.includes(a.name))
                .filter((attribute) =>
                    attribute.name
                        .toLowerCase()
                        .trim()
                        .includes(search.toLowerCase().trim()),
                ),
        [props.attributes, rankedNames, search],
    );

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        }),
    );

    useEffect(() => {
        const val = parseFloat(maxBuyingPower);
        if (!isNaN(val)) {
            props.rankingContextRef.current.max_borrowing_potential = val;
        } else {
            delete props.rankingContextRef.current.max_borrowing_potential;
        }
        props.rankingContextRef.current.method = method;
        props.rankingContextRef.current.target = target;
        const pNumeric = parseFloat(pInput);
        if (method === "power_mean" && !isNaN(pNumeric)) {
            props.rankingContextRef.current.p = pNumeric;
        } else {
            delete props.rankingContextRef.current.p;
        }
        if (target === "places") {
            props.rankingContextRef.current.place_level = placeLevel;
        } else {
            delete props.rankingContextRef.current.place_level;
        }
        if (anchors.length > 0) {
            props.rankingContextRef.current.anchors = anchors.map(
                ({ lat, lng, modes }) => ({ lat, lng, modes }),
            );
        } else {
            delete props.rankingContextRef.current.anchors;
        }

        const household = {
            adults: parseInt(adultsParam) || 0,
            child_0_5: parseInt(child05Param) || 0,
            child_6_12: parseInt(child612Param) || 0,
            child_13_plus: parseInt(child13Param) || 0,
        };
        const householdSize =
            household.adults +
            household.child_0_5 +
            household.child_6_12 +
            household.child_13_plus;
        if (householdSize > 0) {
            props.rankingContextRef.current.household = household;
        } else {
            delete props.rankingContextRef.current.household;
        }

        const map = props.map.current;
        if (!map) return;

        if (map.getSource("mvt-tiles")) {
            if (map.getLayer("tiles")) map.removeLayer("tiles");
            if (map.getLayer("tiles-values")) map.removeLayer("tiles-values");
            map.removeSource("mvt-tiles");
        }

        if (rankedItems.length === 0) return;

        props.rankingContextRef.current.stack_rank = rankedItems.map(
            (item) => item.name,
        );

        // Scored individual plots (property polygons), only at close zoom.
        const isProperties = target === "properties";
        const tileUrl =
            target === "places"
                ? `${process.env.BUN_PUBLIC_TILE_SERVER}/api/tiles/rank_places/${placeLevel}/{z}/{x}/{y}?${getEnvParam()}`
                : isProperties
                  ? `${process.env.BUN_PUBLIC_TILE_SERVER}/api/tiles/rank_properties/{z}/{x}/{y}?${getEnvParam()}`
                  : `${process.env.BUN_PUBLIC_TILE_SERVER}/api/tiles/rank/{z}/{x}/{y}?${getEnvParam()}`;
        const sourceLayer =
            target === "places"
                ? "places"
                : isProperties
                  ? "property_scores"
                  : "values";

        map.addSource("mvt-tiles", {
            type: "vector",
            tiles: [tileUrl],
            ...(isProperties ? { minzoom: 14 } : {}),
        });

        map.addLayer({
            id: "tiles",
            type: "fill",
            source: "mvt-tiles",
            "source-layer": sourceLayer,
            paint: createMapboxFillAndOpacity(["get", "value"]),
        });

        // The per-feature value label is noise on dense plots, so only the cell /
        // place layers get it.
        if (!isProperties) {
            map.addLayer({
                id: "tiles-values",
                type: "symbol",
                source: "mvt-tiles",
                "source-layer": sourceLayer,
                minzoom: 12.5,
                paint: {
                    "text-color": "#fff",
                    "text-opacity": [
                        "interpolate",
                        ["linear"],
                        ["get", "value"],
                        0,
                        0.4,
                        1,
                        0.6,
                    ],
                },
                layout: {
                    "text-field": ["get", "value"],
                    "text-font": ["Source Code Pro", "Arial Unicode MS Bold"],
                    "text-offset": [0, 0],
                    "text-size": 8,
                    "text-anchor": "center",
                },
            });
        }

        // Clicking a cell/place/plot queries its full breakdown on demand (one
        // entity at a time) and pins it in the right-hand panel.
        const handleClick = async (e: MapMouseEvent) => {
            const feature = e.features?.[0];
            if (!feature) return;
            const context = props.rankingContextRef.current;
            const id = Number(feature.id ?? feature.properties?.id);
            try {
                const breakdown =
                    target === "places"
                        ? await explainPlace(id, context)
                        : isProperties
                          ? await explainProperty(id, map.getZoom(), context)
                          : await explainCell(
                                map.getZoom(),
                                e.lngLat.lng,
                                e.lngLat.lat,
                                context,
                            );
                if (breakdown) props.onSelectScore(breakdown);
            } catch {
                /* ignore transient query failures */
            }
        };
        map.on("click", "tiles", handleClick);

        // Hover shows only the final score; the full breakdown lives in the
        // click-through side panel.
        const popup = new Popup({
            closeButton: false,
            closeOnClick: false,
            maxWidth: "200px",
            className: "rank-popup-container",
        });

        const handleMove = (e: MapMouseEvent) => {
            const feature = e.features?.[0];
            if (!feature) {
                popup.remove();
                return;
            }
            const value = Number(feature.properties?.value ?? 0);
            map.getCanvas().style.cursor = "pointer";
            popup
                .setLngLat(e.lngLat)
                .setHTML(
                    `<div class="rank-popup"><div class="rank-popup__row"><span>Score</span><span>${value.toFixed(3)}</span></div></div>`,
                )
                .addTo(map);
        };
        const handleLeave = () => {
            map.getCanvas().style.cursor = "";
            popup.remove();
        };

        map.on("mousemove", "tiles", handleMove);
        map.on("mouseleave", "tiles", handleLeave);

        return () => {
            map.off("click", "tiles", handleClick);
            map.off("mousemove", "tiles", handleMove);
            map.off("mouseleave", "tiles", handleLeave);
            popup.remove();
        };
    }, [
        rankedItems,
        rankedNames,
        props.map,
        props.rankingContextRef,
        maxBuyingPower,
        method,
        pInput,
        target,
        placeLevel,
        anchors,
        adultsParam,
        child05Param,
        child612Param,
        child13Param,
        props.onSelectScore,
        props.env,
    ]);

    function handleDragEnd(event: DragEndEvent) {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        mutateStack(({ attrs }) => {
            const oldIndex = attrs.indexOf(active.id as string);
            const newIndex = attrs.indexOf(over.id as string);
            if (oldIndex === -1 || newIndex === -1) return {};
            return { attrs: arrayMove(attrs, oldIndex, newIndex) };
        });
    }

    function handleAdd(name: string) {
        setSearch("");
        mutateStack(({ attrs }) => ({ attrs: [...attrs, name] }));
    }

    function handleRemove(name: string) {
        mutateStack(({ attrs }) => ({
            attrs: attrs.filter((n) => n !== name),
        }));
    }

    function handleAnchorAdd(anchor: Anchor) {
        // New anchors join the bottom of the stack rank, ready to be dragged
        // above or below regular attributes.
        mutateStack(({ attrs, anchors }) => ({
            anchors: [...anchors, anchor],
            attrs: [...attrs, anchorName(anchors.length)],
        }));
    }

    function handleAnchorUpdate(index: number, anchor: Anchor) {
        mutateStack(({ anchors }) => ({
            anchors: anchors.map((a, i) => (i === index ? anchor : a)),
        }));
    }

    function handleAnchorRemove(index: number) {
        // Anchor stack-rank references are positional, so drop the removed
        // one and shift the references above it down.
        mutateStack(({ attrs, anchors }) => ({
            anchors: anchors.filter((_, i) => i !== index),
            attrs: attrs.flatMap((name) => {
                const anchorIndex = parseAnchorName(name);
                if (anchorIndex === null) return [name];
                if (anchorIndex === index) return [];
                return [
                    anchorIndex > index ? anchorName(anchorIndex - 1) : name,
                ];
            }),
        }));
    }

    function applyProfile(profileId: string) {
        const profile = RANK_PROFILES.find((p) => p.id === profileId);
        if (!profile) return;
        const validNames = profile.attributes.filter((name) =>
            props.attributes.some((a) => a.name === name),
        );
        // A profile only swaps the attribute set — keep any anchor references
        // already in the stack rank.
        mutateStack(({ attrs }) => ({
            attrs: [
                ...validNames,
                ...attrs.filter((n) => parseAnchorName(n) !== null),
            ],
        }));
    }

    return (
        <div className="rank-container">
            <TabList
                tabs={RANK_TARGET_TABS}
                current={target}
                setCurrent={(t) => updateParams({ target: t })}
            />
            {target === "places" && (
                <div className="place-level-select">
                    <label htmlFor="rank-place-level">Level</label>
                    <select
                        id="rank-place-level"
                        value={placeLevel}
                        onChange={(e) =>
                            updateParams({ level: e.target.value })
                        }
                    >
                        {PLACE_LEVELS.map((l) => (
                            <option key={l.value} value={l.value}>
                                {l.label}
                            </option>
                        ))}
                    </select>
                </div>
            )}
            {target === "properties" && (
                <p className="rank-explain-note">
                    Zoom in (≥14) to see individual plots. Score = location ×
                    household match × affordability; non-matching plots read 0.
                </p>
            )}

            <h4>Profile</h4>
            <select
                className="rank-method-select"
                value=""
                onChange={(e) => {
                    if (e.target.value) {
                        applyProfile(e.target.value);
                        e.target.value = "";
                    }
                }}
            >
                <option value="">Load a profile…</option>
                {RANK_PROFILES.map((p) => (
                    <option key={p.id} value={p.id} title={p.description}>
                        {p.name}
                    </option>
                ))}
            </select>

            <h4>Max Buying Power</h4>
            <div>
                <Input
                    type="number"
                    value={maxBuyingPower}
                    onChange={(e) =>
                        updateParams({ budget: e.target.value || null })
                    }
                    placeholder="Enter amount..."
                    startIcon={<PoundSterling size={16} />}
                />
            </div>

            <h4>Household</h4>
            <div className="household-inputs">
                {(
                    [
                        ["adults", "Adults", adultsParam],
                        ["c05", "Children 0–5", child05Param],
                        ["c612", "Children 6–12", child612Param],
                        ["c13", "Children 13+", child13Param],
                    ] as const
                ).map(([key, label, value]) => (
                    <label key={key}>
                        <span>{label}</span>
                        <input
                            type="number"
                            min={0}
                            value={value}
                            placeholder="0"
                            onChange={(e) =>
                                updateParams({ [key]: e.target.value || null })
                            }
                        />
                    </label>
                ))}
            </div>

            <hr />
            <Anchors
                map={props.map}
                anchors={anchors}
                onAdd={handleAnchorAdd}
                onUpdate={handleAnchorUpdate}
                onRemove={handleAnchorRemove}
                env={props.env}
            />
            <hr />
            <h4>Stack Rank</h4>
            <div className="control-panel-button-list rank-list">
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                    modifiers={[
                        restrictToVerticalAxis,
                        restrictToParentElement,
                    ]}
                >
                    <SortableContext
                        items={rankedItems.map((item) => item.name)}
                        strategy={verticalListSortingStrategy}
                    >
                        {rankedItems.map((item) => (
                            <SortableItem
                                key={item.name}
                                id={item.name}
                                className="control-panel-button-list__item control-panel-button-list__item--active rank-item"
                                onRemove={() =>
                                    item.kind === "anchor"
                                        ? handleAnchorRemove(item.anchorIndex)
                                        : handleRemove(item.name)
                                }
                            >
                                {item.kind === "anchor" ? (
                                    <span className="rank-item__anchor">
                                        <span
                                            className="anchors-item__swatch"
                                            style={{
                                                background: anchorColour(
                                                    item.anchorIndex,
                                                ),
                                            }}
                                        />
                                        Anchor {item.anchorIndex + 1}
                                    </span>
                                ) : (
                                    item.name
                                )}
                            </SortableItem>
                        ))}
                    </SortableContext>
                </DndContext>
                {rankedItems.length === 0 && (
                    <div className="empty-state">
                        Add attributes from below to create a rank.
                    </div>
                )}
            </div>

            <hr />

            <h4>Available Attributes</h4>
            <ControlPanelListSearch
                type="attribute"
                search={search}
                setSearch={setSearch}
            />
            <div className="control-panel-button-list rank-available-list">
                {availableAttributes.map((attribute) => (
                    <button
                        key={attribute.id}
                        onClick={() => handleAdd(attribute.name)}
                        className="control-panel-button-list__item"
                    >
                        <div></div>
                        {attribute.name}
                    </button>
                ))}
            </div>

            <div className="rank-bottom">
                <h4>Ranking method</h4>
                <select
                    className="rank-method-select"
                    value={method}
                    onChange={(e) => updateParams({ method: e.target.value })}
                >
                    {(
                        Object.entries(RANK_METHOD_LABELS) as [
                            RankMethod,
                            string,
                        ][]
                    ).map(([key, label]) => (
                        <option key={key} value={key}>
                            {label}
                        </option>
                    ))}
                </select>
                {method === "power_mean" && (
                    <label className="rank-power-mean-input">
                        <span>
                            p ={" "}
                            <small>
                                (1 = WSM, 0 → WPM, negative = more
                                non-compensatory)
                            </small>
                        </span>
                        <input
                            type="number"
                            step="0.5"
                            value={pInput}
                            onChange={(e) =>
                                updateParams({ p: e.target.value })
                            }
                        />
                    </label>
                )}
                <p className="rank-explain-note">
                    Click a cell or area on the map for its full score breakdown.
                </p>
            </div>
        </div>
    );
}

export default Rank;
